import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Responsive from "@amcharts/amcharts5/themes/Responsive";

const CallChart = () => {
    const chartRef = useRef(null); // DOM reference

    useLayoutEffect(() => {
        if (!chartRef.current) return;

        // Clean old chart if strict mode triggers double mount
        let existingRoot = am5.registry.rootElements.find(
            (r) => r.dom === chartRef.current
        );
        if (existingRoot) existingRoot.dispose();

        // Create root
        const root = am5.Root.new(chartRef.current);

        const myTheme = am5.Theme.new(root);
        myTheme.rule("AxisLabel", ["minor"]).setAll({ dy: 1 });

        root.setThemes([
            am5themes_Animated.new(root),
            myTheme,
            am5themes_Responsive.new(root),
        ]);

        // Chart
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "panX",
                wheelY: "zoomX",
                paddingLeft: 0,
            })
        );

        const cursor = chart.set(
            "cursor",
            am5xy.XYCursor.new(root, { behavior: "zoomX" })
        );
        cursor.lineY.set("visible", false);

        // Generate data
        let date = new Date();
        date.setHours(0, 0, 0, 0);
        let value = 100;

        function generateData() {
            value = Math.round(Math.random() * 10 - 5 + value);
            am5.time.add(date, "day", 1);
            return { date: date.getTime(), value };
        }

        function generateDatas(count) {
            let data = [];
            for (let i = 0; i < count; i++) data.push(generateData());
            return data;
        }

        const data = generateDatas(30);

        // Axes
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                maxDeviation: 0,
                baseInterval: { timeUnit: "day", count: 1 },
                renderer: am5xy.AxisRendererX.new(root, {
                    minorGridEnabled: true,
                    minorLabelsEnabled: true,
                }),
                tooltip: am5.Tooltip.new(root, {}),
            })
        );

        xAxis.set("minorDateFormats", { day: "dd", month: "MM" });

        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
            })
        );

        // Series
        const series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: "Series",
                xAxis,
                yAxis,
                valueYField: "value",
                valueXField: "date",
                tooltip: am5.Tooltip.new(root, {
                    labelText: "{valueY}",
                }),
            })
        );

        series.columns.template.setAll({ strokeOpacity: 0 });

        // Calculate average
        const avg = data.reduce((sum, d) => sum + d.value, 0) / data.length;

        // Color columns based on whether they are below average
        series.columns.template.adapters.add("fill", (fill, target) => {
            const value = target.dataItem.get("valueY");
            return value < avg ? am5.color(0xff0000) : am5.color(0x4caf50); // Red below avg, green above
        });

        series.data.setAll(data);

        series.appear(1000);
        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, []);

    return (
        <div className="bg-white p-4 rounded-xl shadow-md">
            <h3 className="dashboard-title">Call Trend Over Time</h3>
            <div ref={chartRef} className="w-full h-[400px]" />
        </div>
    );
};

export default CallChart;
